<template>
    <wrapper-features-templates-one-column
    buttonFullWidth
    :applyErrorState="applyErrorState && !showLoader"
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
	:showFooter="!showLoader && !applyErrorState && getMobileAppState && getCardsTypeList.length"
	:footerButtonsParams="footerButtonsData"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- Cards Availabled for request list -->
        <selectors-wrapper
        v-if="!showLoader && !applyErrorState && getCardsTypeList.length"
        :applyMobileHeaderProductSlider="getMobileAppState"
        :productsList="cardsList"/>
    </wrapper-features-templates-one-column>
    <!-- Feedback Toasts -->
    <toasts-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
    <!-- Centered Modal falta completar -->
    <modal-centered-generic
    v-if="showCenteredModal"
    :title="centeredModalData.title"
    :text="centeredModalData.text"
    :leftButtonText="centeredModalData.leftButtonText"
    @hideMe="showCenteredModal = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import SelectorsWrapper from '@/components/generic/cards/productSelector/SelectorsWrapper'
import ToastsGeneric from '@/components/generic/toasts/Generic'
/* Vue */
import { ref, computed } from 'vue'

/* Assets & Helpers */
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const { requestCardsTypeList, validateQuantityForRequest } = mapActions(moduleMapper.CARDS_REQUEST)
const { getCardsTypeList } = mapGetters(moduleMapper.CARDS_REQUEST)
const { getMobileAppState } = mapGetters()

/* Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const showCenteredModal = ref(false)
const applyErrorState = ref(false)
const mobileSelectedIndex = ref(0)

/* Footer Buttons Data */
const footerButtonsData = computed(() => {
    if (!getCardsTypeList.value.length) return []

	return [{
		label: selectCardBtnTxt(getCardsTypeList.value[mobileSelectedIndex.value]),
		icon: 'arrow-right-full',
		buttonType: 'primary',
		iconPosition: 'right',
		callbackFunction: () => {
            const selectedProduct = getCardsTypeList.value[mobileSelectedIndex.value].subProductCode
            cardSelectHandler(selectedProduct)
        }
	}]
})

/*************************************************
*                                                *
*                Initial Requets                 *
*                                                *
*************************************************/
const initialRequests = async () => {
    showLoader.value = true
    applyErrorState.value = false
	showFeedbackToast.value = false

    try {
        await requestCardsTypeList({ canBeRequestedFilter: true })
        showLoader.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
        applyErrorState.value = true
		errorHandler(error.response.data, initialRequests)
	}
}

const cardsList = computed(() => {
    if (!getCardsTypeList.value.length) return null

    return getCardsTypeList.value.map((card) => {
        return {
            ...card,
            id: card.subProductCode,
            title: card.description,
            description: card.longDescription,
            imageUrl: card.cardImageUrl,
            list: card.features,
            infoSheetUrl: card.infoSheetUrl,
            infoSheetUrlBtnText: t('cards.request.infoSheetBtn'),
            infoSheetCustomSuccessMessage: t('cards.request.downloadInfoSheetSuccessMessage'),
            secondBtnText: selectCardBtnTxt(card),
            secondBtnClicked: () => cardSelectHandler(card.subProductCode)
        }
    })
})

const selectCardBtnTxt = cardInfo => {
    if (cardInfo.isVirtual) return t('cards.request.generateCardBtn')
    return t('cards.request.requestCardBtn')
}

/*************************************************
*                                                *
*              Select Card Handlers              *
*                                                *
*************************************************/
const selectedCard = ref(null)

const cardSelectHandler = async (cardId) => {
	selectedCard.value = getCardsTypeList.value.find(obj => obj.subProductCode === cardId)
    selectedCard.value.subProductCode === 'CVISA_VIRT' ? goToRequestRoute() : validateCardsQuantity()
}

const validateCardsQuantity = async () => {
    showOverlayLoader.value = true
	showFeedbackToast.value = false

    try {
        await validateQuantityForRequest({ typeCode: selectedCard.value.subProductCode })
        goToRequestRoute()
	} catch (error) {
		if (error.toString() === 'Cancel') return
        if (error.response.data.code === 'card_solicitation_limit_reached') {
            centeredModalDataHandler(error.response.data)
        } else errorHandler(error.response.data, validateCardsQuantity)
	}
}

const centeredModalData = ref({
	title: '',
	text: '',
    leftButtonText: ''
})

const centeredModalDataHandler = data => {
    showOverlayLoader.value = false

    centeredModalData.value = {
		title: t('cards.request.modalCentered.title'),
		text: data.message,
        leftButtonText: t('cards.request.modalCentered.leftButtonText')
	}
	showCenteredModal.value = true
}

const goToRequestRoute = () => {
    let routeName = ''

    switch (selectedCard.value.subProductCode) {
        case 'CVISA_VIRT':
            routeName = routeList.CARDS_REQUEST_CARD_CVISA_VIRT_NAME
            break
        /* This view will handle card requests based only on info provided by mdw and will create the form dynamic */
        default:
            routeName = routeList.CARDS_REQUEST_CARD_GENERIC_NAME
            break
    }

    navigateByName(routeName, { selectedCardInfo: selectedCard.value })
}
/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
    showLoader.value = false
    showOverlayLoader.value = false

    feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

initialRequests()
</script>

<style lang="scss" scoped>
</style>
